package com.evoapps.evoshift.widgets

import androidx.compose.runtime.setValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.rememberCoroutineScope
import com.evoapps.evoshift.ui.theme.motionContentSize
import com.evoapps.evoshift.ui.theme.motionTween
import com.evoapps.evoshift.ui.theme.LocalReducedMotion
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.Icons
import androidx.compose.ui.semantics.Role
import androidx.compose.foundation.clickable
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.ProvideTextStyle
import androidx.compose.material3.SheetState
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.paneTitle
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.foundation.focusable
import androidx.compose.runtime.rememberUpdatedState
import kotlinx.coroutines.launch
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isCtrlPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.window.DialogProperties
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DarkMetallicBackgroundBrush
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.GoldMetallicBrush
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.cancel
import evoshift.composeapp.generated.resources.close
import androidx.compose.material.icons.filled.Close
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.layout.safeDrawing
import org.jetbrains.compose.resources.stringResource

/*
 * The app's one dialog language: a navy metallic card with a thin gold edge, a sticky header and
 * footer, and a body that scrolls when it does not fit (so actions never scroll away and no
 * dialog runs off a phone screen). Confirmations, forms and sheets all use these.
 */

/** A button in a dialog footer. [loading] keeps it gold with a spinner and ignores taps. */
data class DialogAction(
    val label: String,
    val onClick: () -> Unit,
    val enabled: Boolean = true,
    val loading: Boolean = false,
    /** Red instead of gold (or a red outline for a secondary action). */
    val destructive: Boolean = false,
    val icon: ImageVector? = null
)

/** How wide a dialog may grow on tablets and desktops (phones always fill the width). */
enum class DialogWidth(val max: Dp) {
    /** Confirmations and one-field prompts. */
    Compact(440.dp),
    /** Most forms. */
    Regular(560.dp),
    /** Long forms with side-by-side fields. */
    Wide(720.dp),
    /** A chart or a table shown full size. */
    Full(1280.dp)
}

private val DialogShape = RoundedCornerShape(24.dp)
private val ButtonShape = RoundedCornerShape(12.dp)

/** Red primary button: a deep-to-bright gradient like the gold one. */
private val DestructiveBrush = Brush.linearGradient(listOf(Color(0xFFA4161A), Color(0xFFD64541), Color(0xFFA4161A)))

/** Thin gold edge, brighter at the top-left like the metallic cards. */
private val GoldEdgeBrushes = com.evoapps.evoshift.ui.PerAppearance { p ->
    Brush.linearGradient(listOf(p.accent.bright.copy(alpha = 0.9f), p.accent.metallic.copy(alpha = 0.35f), p.accent.metallic.copy(alpha = 0.7f)))
}
private val GoldEdgeBrush: Brush get() = GoldEdgeBrushes.current

/** Below this width the footer stacks its buttons. */
private val StackButtonsBelow = 340.dp

/**
 * Below this window height (a phone in landscape) the header scrolls with the body and the
 * footer is a single compact row, so the fields keep most of the height (SH-83).
 */
internal val ShortDialogBelow = 480.dp

/**
 * A dialog in the app's navy and gold style.
 *
 * - The header ([icon] in a gold ring, [title], [subtitle]) and the footer ([dismiss], [neutral],
 *   [confirm]) stay put; [content] scrolls inside at most 90% of the window height.
 * - On a short window (a phone in landscape, [ShortDialogBelow]) the header scrolls with the
 *   content and the footer is one compact row: the buttons never take the fields' height.
 * - [destructive] turns the confirm button red. [dismiss] defaults to Cancel; pass null for a
 *   dialog that only acknowledges.
 * - [dismissible] = false blocks back, outside taps and the dismiss button (e.g. while
 *   something is being written).
 */
@Composable
fun EvoDialog(
    title: String,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    icon: ImageVector? = null,
    subtitle: String? = null,
    confirm: DialogAction? = null,
    dismiss: DialogAction? = DialogAction(stringResource(Res.string.cancel), onDismiss),
    neutral: DialogAction? = null,
    destructive: Boolean = false,
    width: DialogWidth = DialogWidth.Regular,
    dismissible: Boolean = true,
    dismissOnClickOutside: Boolean = true,
    scrollState: ScrollState = rememberScrollState(),
    bodyArrangement: Arrangement.Vertical = Arrangement.spacedBy(16.dp),
    content: @Composable ColumnScope.() -> Unit
) {
    // Fade and grow in: cheap on every platform (one graphics layer). SH-158: closing it (scrim,
    // back, Esc, the plain Cancel) plays the same in reverse before [onDismiss] removes it.
    val reduced = LocalReducedMotion.current
    val appear = remember { Animatable(0f) }
    LaunchedEffect(Unit) { appear.animateTo(1f, motionTween(DIALOG_APPEAR_MS, reduced)) }
    val scope = rememberCoroutineScope()
    val latestDismiss by rememberUpdatedState(onDismiss)
    var closing by remember { mutableStateOf(false) }
    val close: () -> Unit = remember(scope) {
        {
            if (!closing) {
                closing = true
                scope.launch {
                    appear.animateTo(0f, motionTween(DIALOG_DISAPPEAR_MS, reduced))
                    latestDismiss()
                }
            }
        }
    }
    // The default Cancel is [onDismiss] itself: it closes the same way.
    val dismissAction = dismiss?.let { if (it.onClick === onDismiss) it.copy(onClick = close) else it }
    val cardFocus = remember { FocusRequester() }
    Dialog(
        onDismissRequest = { if (dismissible) close() },
        properties = DialogProperties(
            dismissOnBackPress = dismissible,
            dismissOnClickOutside = dismissible && dismissOnClickOutside,
            usePlatformDefaultWidth = false
        )
    ) {
        // Light text and the dialog's colour scheme for everything inside (navy on navy otherwise).
        ProvideDialogKeyboardDismiss(cardFocus) { ProvideDialogColors {
            BoxWithConstraints(
                modifier = Modifier.imePadding(),
                contentAlignment = Alignment.Center
            ) {
                val sideMargin = if (maxWidth < 600.dp) 16.dp else 24.dp
                val maxCardHeight = maxHeight * 0.9f
                // SH-83: a phone in landscape. The header scrolls away with the body (the fields get
                // the height) and the footer is one compact row instead of stacked buttons.
                val short = maxHeight < ShortDialogBelow
                val stackButtons = !short && (maxWidth - sideMargin * 2 < StackButtonsBelow || neutral != null)

                Column(
                    modifier = modifier
                        .padding(horizontal = sideMargin)
                        .widthIn(max = width.max)
                        .fillMaxWidth()
                        .heightIn(max = maxCardHeight)
                        .graphicsLayer {
                            alpha = appear.value
                            val scale = 0.94f + 0.06f * appear.value
                            scaleX = scale
                            scaleY = scale
                        }
                        .shadow(elevation = 24.dp, shape = DialogShape, ambientColor = Color.Black, spotColor = Color.Black)
                        .clip(DialogShape)
                        .background(DarkMetallicBackgroundBrush)
                        .border(BorderStroke(1.dp, GoldEdgeBrush), DialogShape)
                        .semantics { paneTitle = title }
                        .testTag(EvoDialogTags.DIALOG)
                        // SH-98: Esc closes, Enter (Ctrl+Enter in a text box) confirms.
                        .dialogKeys(
                            onEscape = { if (dismissible) close() },
                            onEnter = confirm?.takeIf { it.enabled && !it.loading }?.onClick,
                            focus = cardFocus
                        )
                ) {
                    if (!short) DialogHeader(title = title, subtitle = subtitle, icon = icon, destructive = destructive)

                    // The body takes what is left between header and footer, and scrolls.
                    Box(Modifier.weight(1f, fill = false)) {
                        Column(Modifier.fillMaxWidth().verticalScroll(scrollState)) {
                            if (short) DialogHeader(title = title, subtitle = subtitle, icon = icon, destructive = destructive, compact = true)
                            ProvideTextStyle(MaterialTheme.typography.bodyMedium) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        // SH-158: a line coming or going (an error, a field) resizes it smoothly.
                                        .motionContentSize()
                                        .padding(horizontal = 24.dp, vertical = 8.dp)
                                        .testTag(EvoDialogTags.BODY),
                                    verticalArrangement = bodyArrangement,
                                    content = content
                                )
                            }
                        }
                        ScrollEdge(visible = scrollState.canScrollBackward, top = true, modifier = Modifier.align(Alignment.TopCenter))
                        ScrollEdge(visible = scrollState.canScrollForward, top = false, modifier = Modifier.align(Alignment.BottomCenter))
                    }

                    val actions = listOfNotNull(dismissAction?.takeIf { dismissible }, neutral, confirm)
                    if (actions.isNotEmpty()) {
                        DialogFooter(
                            confirm = confirm,
                            dismiss = dismissAction?.takeIf { dismissible },
                            neutral = neutral,
                            destructive = destructive,
                            stacked = stackButtons,
                            compact = short
                        )
                    } else {
                        Spacer(Modifier.height(if (short) 12.dp else 20.dp))
                    }
                }
            }
        } }
    }
}

/**
 * SH-98: a dialog's or sheet's keyboard: Esc calls [onEscape]; Enter calls [onEnter] unless
 * something inside used it (a button presses itself, a text box adds a line); Ctrl+Enter calls it
 * from anywhere in the dialog. The dialog takes the focus when it opens, so the keys reach it on
 * a laptop before anything was clicked (a field that asks for the focus itself still gets it).
 */
@Composable
internal fun Modifier.dialogKeys(
    onEscape: (() -> Unit)?,
    onEnter: (() -> Unit)?,
    focus: FocusRequester = remember { FocusRequester() }
): Modifier {
    LaunchedEffect(focus) { runCatching { focus.requestFocus() } }
    val escape by rememberUpdatedState(onEscape)
    val enter by rememberUpdatedState(onEnter)
    return this
        .onPreviewKeyEvent { event ->
            val action = enter
            if (event.type == KeyEventType.KeyDown && event.isCtrlPressed && event.key.isEnter() && action != null) {
                action()
                true
            } else false
        }
        .onKeyEvent { event ->
            if (event.type != KeyEventType.KeyDown) return@onKeyEvent false
            when {
                event.key == Key.Escape -> escape?.let { it(); true } ?: false
                event.key.isEnter() -> enter?.let { it(); true } ?: false
                else -> false
            }
        }
        .focusRequester(focus)
        .focusable()
        // SH-249: a tap on the dialog's background puts the keyboard away.
        .dismissKeyboardOnTapOutside()
}

/**
 * SH-249: [ProvideKeyboardDismiss] for a dialog or sheet (its own window): Done and a tap on its
 * background give the focus back to the dialog ([focus], the one passed to [dialogKeys]), so the
 * keyboard goes and Esc / Enter still reach the dialog.
 */
@Composable
internal fun ProvideDialogKeyboardDismiss(focus: FocusRequester, content: @Composable () -> Unit) =
    ProvideKeyboardDismiss(onDismiss = { runCatching { focus.requestFocus() } }, content = content)

private fun Key.isEnter() = this == Key.Enter || this == Key.NumPadEnter

/** How long a dialog takes to appear, and to go (a little quicker: the answer is already given). */
internal const val DIALOG_APPEAR_MS = 160
internal const val DIALOG_DISAPPEAR_MS = 120

/** Test tags of [EvoDialog], for UI tests. */
object EvoDialogTags {
    const val DIALOG = "evo_dialog"
    const val BODY = "evo_dialog_body"
    const val CONFIRM = "evo_dialog_confirm"
    const val DISMISS = "evo_dialog_dismiss"
    const val NEUTRAL = "evo_dialog_neutral"
}

/** A gold hairline and a soft fade at the edge where content is scrolled out of sight. */
@Composable
private fun ScrollEdge(visible: Boolean, top: Boolean, modifier: Modifier) {
    val alpha by animateFloatAsState(if (visible) 1f else 0f, tween(150))
    val fade = listOf(DeepNavy.copy(alpha = 0.85f), Color.Transparent)
    Box(
        modifier
            .fillMaxWidth()
            .height(14.dp)
            .graphicsLayer { this.alpha = alpha }
            .background(Brush.verticalGradient(if (top) fade else fade.reversed()))
    ) {
        Box(
            Modifier
                .align(if (top) Alignment.TopCenter else Alignment.BottomCenter)
                .fillMaxWidth()
                .height(1.dp)
                .background(Brush.horizontalGradient(listOf(Color.Transparent, DarkGold.copy(alpha = 0.6f), Color.Transparent)))
        )
    }
}

@Composable
private fun DialogHeader(title: String, subtitle: String?, icon: ImageVector?, destructive: Boolean, compact: Boolean = false) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(start = 24.dp, end = 24.dp, top = if (compact) 16.dp else 24.dp, bottom = if (compact) 4.dp else 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (icon != null) {
            val ring: Brush = if (destructive) SolidColor(ErrorOnNavy) else GoldMetallicBrush
            Box(
                modifier = Modifier
                    .size(44.dp)
                    .clip(CircleShape)
                    .background(Color.White.copy(alpha = 0.06f))
                    .border(1.5.dp, ring, CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(icon, contentDescription = null, tint = if (destructive) ErrorOnNavy else LightGold, modifier = Modifier.size(22.dp))
            }
            Spacer(Modifier.width(16.dp))
        }
        Column(Modifier.weight(1f)) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = LightGold,
                modifier = Modifier.semantics { heading() }
            )
            subtitle?.let {
                Spacer(Modifier.height(2.dp))
                Text(it, style = MaterialTheme.typography.bodyMedium, color = Color.White.copy(alpha = 0.7f))
            }
        }
    }
}

@Composable
private fun DialogFooter(
    confirm: DialogAction?,
    dismiss: DialogAction?,
    neutral: DialogAction?,
    destructive: Boolean,
    stacked: Boolean,
    compact: Boolean = false
) {
    val footerModifier = Modifier
        .fillMaxWidth()
        .padding(start = 24.dp, end = 24.dp, top = if (compact) 8.dp else 16.dp, bottom = if (compact) 12.dp else 20.dp)
    if (stacked) {
        // Narrow windows and three actions: full-width buttons, the main one on top.
        Column(footerModifier, verticalArrangement = Arrangement.spacedBy(10.dp)) {
            confirm?.let {
                EvoPrimaryButton(it.copy(destructive = it.destructive || destructive), Modifier.fillMaxWidth().testTag(EvoDialogTags.CONFIRM))
            }
            neutral?.let { EvoSecondaryButton(it, Modifier.fillMaxWidth().testTag(EvoDialogTags.NEUTRAL)) }
            dismiss?.let { EvoSecondaryButton(it, Modifier.fillMaxWidth().testTag(EvoDialogTags.DISMISS), quiet = true) }
        }
    } else {
        Row(footerModifier, horizontalArrangement = Arrangement.spacedBy(12.dp, Alignment.End), verticalAlignment = Alignment.CenterVertically) {
            dismiss?.let { EvoSecondaryButton(it, Modifier.weight(1f, fill = confirm != null).testTag(EvoDialogTags.DISMISS)) }
            // A short window keeps the neutral action in the row: Cancel, it, then the main one.
            neutral?.let { EvoSecondaryButton(it, Modifier.weight(1f).testTag(EvoDialogTags.NEUTRAL)) }
            confirm?.let {
                EvoPrimaryButton(it.copy(destructive = it.destructive || destructive), Modifier.weight(1f).testTag(EvoDialogTags.CONFIRM))
            }
        }
    }
}

/** The gold (or red) main button of dialogs and sheets: Button semantics, disabled and busy states. */
@Composable
fun EvoPrimaryButton(action: DialogAction, modifier: Modifier = Modifier) {
    val active = action.enabled || action.loading
    val background: Brush = when {
        !active -> SolidColor(Color.White.copy(alpha = 0.12f))
        action.destructive -> DestructiveBrush
        else -> GoldMetallicBrush
    }
    val content = if (action.destructive) Color.White else DeepNavy
    BrushButton(
        onClick = action.onClick,
        brush = background,
        shape = ButtonShape,
        contentColor = content,
        disabledContentColor = if (action.loading) content else Color.White.copy(alpha = 0.45f),
        enabled = action.enabled && !action.loading,
        modifier = modifier.heightIn(min = 48.dp)
    ) {
        ButtonContent(action, spinnerColor = content)
    }
}

/** The outlined secondary button; [quiet] uses a softer outline for Cancel under stacked buttons. */
@Composable
fun EvoSecondaryButton(action: DialogAction, modifier: Modifier = Modifier, quiet: Boolean = false) {
    val color = if (action.destructive) ErrorOnNavy else Color.White
    OutlinedButton(
        onClick = action.onClick,
        enabled = action.enabled && !action.loading,
        shape = ButtonShape,
        border = BorderStroke(
            1.dp,
            when {
                action.destructive -> ErrorOnNavy.copy(alpha = 0.7f)
                quiet -> Color.White.copy(alpha = 0.2f)
                else -> Color.White.copy(alpha = 0.35f)
            }
        ),
        colors = ButtonDefaults.outlinedButtonColors(
            contentColor = color,
            disabledContentColor = if (action.loading) color else color.copy(alpha = 0.4f)
        ),
        modifier = modifier.heightIn(min = 48.dp).handCursor()
    ) {
        ButtonContent(action, spinnerColor = color)
    }
}

@Composable
private fun ButtonContent(action: DialogAction, spinnerColor: Color) {
    if (action.loading) {
        CircularProgressIndicator(modifier = Modifier.size(18.dp), color = spinnerColor, strokeWidth = 2.dp)
        Spacer(Modifier.width(10.dp))
    } else if (action.icon != null) {
        Icon(action.icon, contentDescription = null, modifier = Modifier.size(18.dp))
        Spacer(Modifier.width(8.dp))
    }
    Text(action.label, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center, maxLines = 2)
}

/**
 * A tappable row of a pick-one list inside a dialog or sheet (a template, a person): a soft
 * card with a headline, an optional second line, and a chevron or [trailing] content.
 */
@Composable
fun EvoListRow(
    headline: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    supporting: String? = null,
    leadingIcon: ImageVector? = null,
    trailing: @Composable (() -> Unit)? = null
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = 56.dp)
            .clip(RoundedCornerShape(14.dp))
            .background(Color.White.copy(alpha = 0.06f))
            .border(1.dp, Color.White.copy(alpha = 0.08f), RoundedCornerShape(14.dp))
            .handCursor()
            .clickable(role = Role.Button, onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (leadingIcon != null) {
            Icon(leadingIcon, contentDescription = null, tint = LightGold, modifier = Modifier.size(20.dp))
            Spacer(Modifier.width(14.dp))
        }
        Column(Modifier.weight(1f)) {
            Text(headline, color = Color.White, fontWeight = FontWeight.SemiBold, style = MaterialTheme.typography.bodyLarge)
            supporting?.let {
                Text(it, color = Color.White.copy(alpha = 0.6f), style = MaterialTheme.typography.bodySmall)
            }
        }
        if (trailing != null) {
            trailing()
        } else {
            Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = null, tint = DarkGold)
        }
    }
}

/** A yes/no question on top of [EvoDialog]. [loading] shows progress on the confirm button and blocks dismissal. */
@Composable
fun EvoConfirmDialog(
    title: String,
    text: String,
    confirmLabel: String,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
    destructive: Boolean = false,
    icon: ImageVector? = null,
    loading: Boolean = false,
    dismissLabel: String = stringResource(Res.string.cancel)
) {
    EvoDialog(
        title = title,
        onDismiss = onDismiss,
        icon = icon,
        destructive = destructive,
        width = DialogWidth.Compact,
        dismissible = !loading,
        confirm = DialogAction(confirmLabel, onConfirm, loading = loading),
        dismiss = DialogAction(dismissLabel, onDismiss)
    ) {
        Text(text, style = MaterialTheme.typography.bodyLarge, color = Color.White.copy(alpha = 0.85f))
    }
}

/**
 * SH-158: closes a sheet the way a swipe does: it slides down first, then [onClosed] removes it
 * (setting the "shown" flag at once makes the sheet vanish without its exit).
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun rememberSheetCloser(sheetState: SheetState, onClosed: () -> Unit): () -> Unit {
    val scope = androidx.compose.runtime.rememberCoroutineScope()
    val latest by rememberUpdatedState(onClosed)
    return remember(sheetState, scope) {
        { scope.launch { sheetState.hide() }.invokeOnCompletion { latest() } }
    }
}

/**
 * A bottom sheet in the dialog style: navy metallic, a gold drag handle, an optional sticky
 * header, and a body that scrolls ([scrollable] = false when [content] has its own lazy list).
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EvoBottomSheet(
    onDismiss: () -> Unit,
    title: String? = null,
    subtitle: String? = null,
    icon: ImageVector? = null,
    sheetState: SheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true),
    scrollable: Boolean = true,
    headerActions: @Composable (() -> Unit)? = null,
    snackbarHostState: androidx.compose.material3.SnackbarHostState? = null,
    content: @Composable ColumnScope.() -> Unit
) {
    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = sheetState,
        shape = RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp),
        containerColor = DeepNavy,
        contentColor = Color.White,
        dragHandle = null
    ) {
        val sheetFocus = remember { FocusRequester() }
        ProvideDialogKeyboardDismiss(sheetFocus) { ProvideDialogColors {
            // SH-158: a modal sheet covers the screen's snackbars; given a host state, the sheet shows
            // them itself (over its bottom edge), e.g. "Shift deleted." with Undo.
            Box(Modifier.fillMaxWidth()) {
                Column(
                    Modifier
                        .fillMaxWidth()
                        .background(DarkMetallicBackgroundBrush)
                        .imePadding()
                        // SH-98: Esc closes the sheet.
                        .dialogKeys(onEscape = onDismiss, onEnter = null, focus = sheetFocus)
                ) {
                    // Gold handle on the sheet's own background (the default one sits on a flat colour).
                    Box(
                        Modifier
                            .padding(top = 12.dp, bottom = 4.dp)
                            .align(Alignment.CenterHorizontally)
                            .size(width = 40.dp, height = 4.dp)
                            .clip(RoundedCornerShape(2.dp))
                            .background(GoldMetallicBrush)
                    )
                    if (title != null) {
                        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                            Box(Modifier.weight(1f)) {
                                DialogHeader(title = title, subtitle = subtitle, icon = icon, destructive = false)
                            }
                            headerActions?.let {
                                Row(Modifier.padding(end = 12.dp, top = 12.dp)) { it() }
                            }
                        }
                    }
                    if (scrollable) {
                        val scroll = rememberScrollState()
                        Box(Modifier.weight(1f, fill = false)) {
                            Column(
                                Modifier
                                    .fillMaxWidth()
                                    .verticalScroll(scroll)
                                    .padding(start = 24.dp, end = 24.dp, top = 8.dp, bottom = 32.dp),
                                verticalArrangement = Arrangement.spacedBy(12.dp),
                                content = content
                            )
                            ScrollEdge(visible = scroll.canScrollBackward, top = true, modifier = Modifier.align(Alignment.TopCenter))
                        }
                    } else {
                        Column(Modifier.fillMaxWidth().weight(1f, fill = false), content = content)
                    }
                }
                snackbarHostState?.let { host ->
                    androidx.compose.material3.SnackbarHost(host, Modifier.align(Alignment.BottomCenter).navigationBarsPadding())
                }
            }
        } }
    }
}

/**
 * SH-229: a tool that takes a step or two (Copy shifts) and needs the room: on a phone a
 * full-screen sheet, elsewhere the dialog card. Either way a header with the [title], the
 * [subtitle] (e.g. what is being copied) and a close ✕ instead of Cancel; the body scrolls; one
 * bottom bar with [back] as a text button on the left and the [confirm] gold button on the right.
 * [confirm]'s label says what to do while it is disabled ("Pick the days"), never a bare grey "Copy".
 * Test tags are [EvoDialogTags]' (the ✕ is [EvoDialogTags.DISMISS], back [EvoDialogTags.NEUTRAL]).
 */
@Composable
fun EvoToolDialog(
    title: String,
    onDismiss: () -> Unit,
    confirm: DialogAction,
    modifier: Modifier = Modifier,
    subtitle: String? = null,
    icon: ImageVector? = null,
    back: DialogAction? = null,
    width: DialogWidth = DialogWidth.Compact,
    scrollState: ScrollState = rememberScrollState(),
    bodyArrangement: Arrangement.Vertical = Arrangement.spacedBy(16.dp),
    content: @Composable ColumnScope.() -> Unit
) {
    val fullScreen = com.evoapps.evoshift.ui.adaptive.LocalWindowWidthClass.current.isCompact
    val reduced = LocalReducedMotion.current
    val appear = remember { Animatable(0f) }
    LaunchedEffect(Unit) { appear.animateTo(1f, motionTween(DIALOG_APPEAR_MS, reduced)) }
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(dismissOnBackPress = true, dismissOnClickOutside = !fullScreen, usePlatformDefaultWidth = false)
    ) {
        val toolFocus = remember { FocusRequester() }
        ProvideDialogKeyboardDismiss(toolFocus) { ProvideDialogColors {
            BoxWithConstraints(Modifier.imePadding(), contentAlignment = Alignment.Center) {
                val frame = if (fullScreen) {
                    Modifier
                        .fillMaxWidth()
                        .heightIn(min = maxHeight, max = maxHeight)
                        .background(DarkMetallicBackgroundBrush)
                        .windowInsetsPadding(androidx.compose.foundation.layout.WindowInsets.safeDrawing)
                } else {
                    Modifier
                        .padding(horizontal = 24.dp)
                        .widthIn(max = width.max)
                        .fillMaxWidth()
                        .heightIn(max = maxHeight * 0.9f)
                        .shadow(elevation = 24.dp, shape = DialogShape, ambientColor = Color.Black, spotColor = Color.Black)
                        .clip(DialogShape)
                        .background(DarkMetallicBackgroundBrush)
                        .border(BorderStroke(1.dp, GoldEdgeBrush), DialogShape)
                }
                Column(
                    modifier
                        .then(frame)
                        .graphicsLayer { alpha = appear.value }
                        .semantics { paneTitle = title }
                        .testTag(EvoDialogTags.DIALOG)
                        .dialogKeys(onEscape = onDismiss, onEnter = confirm.takeIf { it.enabled && !it.loading }?.onClick, focus = toolFocus)
                ) {
                    ToolHeader(title, subtitle, icon = icon.takeIf { !fullScreen }, onClose = onDismiss)
                    Box(Modifier.fillMaxWidth().height(1.dp).background(GoldMetallicBrush.takeIf { fullScreen } ?: SolidColor(Color.White.copy(alpha = 0.08f))))
                    Box(Modifier.weight(1f, fill = fullScreen)) {
                        Column(Modifier.fillMaxWidth().verticalScroll(scrollState)) {
                            ProvideTextStyle(MaterialTheme.typography.bodyMedium) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .motionContentSize()
                                        .padding(horizontal = if (fullScreen) 16.dp else 24.dp, vertical = 12.dp)
                                        .testTag(EvoDialogTags.BODY),
                                    verticalArrangement = bodyArrangement,
                                    content = content
                                )
                            }
                        }
                        ScrollEdge(visible = scrollState.canScrollBackward, top = true, modifier = Modifier.align(Alignment.TopCenter))
                        ScrollEdge(visible = scrollState.canScrollForward, top = false, modifier = Modifier.align(Alignment.BottomCenter))
                    }
                    ToolBottomBar(back, confirm)
                }
            }
        } }
    }
}

/** The tool's header: its icon (wide only), title, subtitle and the close ✕. */
@Composable
private fun ToolHeader(title: String, subtitle: String?, icon: ImageVector?, onClose: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(start = if (icon != null) 24.dp else 16.dp, end = 4.dp, top = 8.dp, bottom = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (icon != null) {
            Box(
                modifier = Modifier.size(40.dp).clip(CircleShape).background(Color.White.copy(alpha = 0.06f)).border(1.5.dp, GoldMetallicBrush, CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(icon, contentDescription = null, tint = LightGold, modifier = Modifier.size(20.dp))
            }
            Spacer(Modifier.width(14.dp))
        }
        Column(Modifier.weight(1f).padding(vertical = 6.dp)) {
            Text(title, style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold, color = LightGold, modifier = Modifier.semantics { heading() })
            subtitle?.let {
                Spacer(Modifier.height(2.dp))
                Text(it, style = MaterialTheme.typography.bodyMedium, color = Color.White.copy(alpha = 0.75f))
            }
        }
        androidx.compose.material3.IconButton(onClick = onClose, modifier = Modifier.handCursor().testTag(EvoDialogTags.DISMISS)) {
            Icon(androidx.compose.material.icons.Icons.Default.Close, stringResource(Res.string.close), tint = Color.White)
        }
    }
}

/** The tool's one bottom bar: back as a text button on the left, the main gold button on the right. */
@Composable
private fun ToolBottomBar(back: DialogAction?, confirm: DialogAction) {
    Column(Modifier.fillMaxWidth()) {
        Box(Modifier.fillMaxWidth().height(1.dp).background(Color.White.copy(alpha = 0.08f)))
        Row(
            Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (back != null) {
                androidx.compose.material3.TextButton(
                    onClick = back.onClick,
                    enabled = back.enabled,
                    modifier = Modifier.heightIn(min = 48.dp).handCursor().testTag(EvoDialogTags.NEUTRAL)
                ) {
                    Text(back.label, color = Color.White, fontWeight = FontWeight.Bold)
                }
            }
            Spacer(Modifier.weight(1f).width(12.dp))
            EvoPrimaryButton(confirm, Modifier.widthIn(min = 140.dp).testTag(EvoDialogTags.CONFIRM))
        }
    }
}
